/*
 * Copyright 2023 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package org.multipaz.securearea

import kotlinx.coroutines.CancellationException
import org.multipaz.crypto.Algorithm
import org.multipaz.crypto.EcPublicKey
import org.multipaz.crypto.SecretKey
import org.multipaz.crypto.SecureByteString
import org.multipaz.crypto.Signature
import org.multipaz.prompt.Reason

/**
 * An interface to a Secure Area.
 *
 * This interface exists to abstract the underlying secure area used
 * used for creation of key material and other security objects related
 * to identity documents and associated credentials.
 *
 * A Secure Area may require authentication before a key can be used and
 * this is modeled through the [KeyLockedException] and [KeyUnlockData]
 * types. By default, [KeyUnlockInteractive] is used which handles user
 * authentication out-of-band so the application will not have to worry
 * about this except to ensure that their user interface can handle platform
 * or local UI being shown to the user for authentication.
 *
 * Existing keys in a Secure Area may be invalidated and this can happen
 * on Android if e.g. the LSKF is removed or if a Cloud-based Secure Area
 * is turned down. This is modeled through the [KeyInvalidatedException]
 * being thrown when attempting to use the key. Applications can also use
 * [getKeyInvalidated] to learn ahead of time if a key is still usable.
 */
interface SecureArea {
    /**
     * A stable identifier for the Secure Area.
     *
     * This is typically just the class name but for secure areas allowing
     * multiple instances, this is of the form className@identifier.
     */
    val identifier: String

    /**
     * The name of the Secure Area, suitable for displaying to the end user.
     */
    val displayName: String

    /**
     * The list of algorithms the Secure Area supports.
     *
     * The algorithms in this list are fully specified.
     */
    val supportedAlgorithms: List<Algorithm>

    /**
     * Creates a new key.
     *
     * This creates an asymmetric key-pair where the private part of the key
     * is never exposed to the user of this interface.
     *
     * The public part of the key is available in [KeyInfo.publicKey] and specific
     * implementations may expose attestations or other proof that the private part of
     * the key never leaves secure hardware.
     *
     * If an existing key with the given alias already exists it will be
     * replaced by the new key.
     *
     * @param alias A unique string to identify the newly created key. When null is passed,
     *     a new unique alias is generated automatically
     * @param createKeySettings A [CreateKeySettings] object.
     * @throws IllegalArgumentException if the underlying Secure Area Implementation
     * does not support the requested creation settings, for example the algorithm or curve to use.
     * @return a [KeyInfo] with information about the key.
     */
    @Throws(
        IllegalArgumentException::class,
        CancellationException::class
    )
    suspend fun createKey(alias: String?, createKeySettings: CreateKeySettings): KeyInfo

    /**
     * Creates a batch of new keys.
     *
     * An [OpenID4VCI Key Attestation](https://openid.github.io/OpenID4VCI/openid-4-verifiable-credential-issuance-wg-draft.html#name-key-attestation-in-jwt-form)
     * over all the keys may be returned. By default this doesn't happen but
     * can be requested by specific implementations through specializations of the [CreateKeySettings] class.
     *
     * The default implementation just calls [createKey] in sequence without generating an OpenID4VCI attestation.
     *
     * @param numKeys number of keys to create.
     * @param createKeySettings the settings for the keys to create.
     * @throws IllegalArgumentException if the underlying Secure Area Implementation
     * does not support the requested creation settings, for example the algorithm or curve to use.
     */
    @Throws(
        IllegalArgumentException::class,
        CancellationException::class
    )
    suspend fun batchCreateKey(numKeys: Int, createKeySettings: CreateKeySettings): BatchCreateKeyResult {
        val keyInfos = mutableListOf<KeyInfo>()
        for (n in 1..numKeys) {
            keyInfos.add(createKey(null, createKeySettings))
        }
        return BatchCreateKeyResult(
            keyInfos = keyInfos,
            openid4vciKeyAttestationJws = null
        )
    }

    /**
     * Deletes a previously created key.
     *
     * If the key to delete doesn't exist, this is a no-op.
     *
     * @param alias The alias of the key to delete.
     */
    @Throws(CancellationException::class)
    suspend fun deleteKey(alias: String)

    /**
     * Signs data with a key.
     *
     * If the key needs unlocking before use (for example user authentication
     * in any shape or form) and `keyUnlockData` isn't set or doesn't contain
     * what's needed, [KeyLockedException] is thrown. Signature algorithm must be specified at key
     * creation time using [CreateKeySettings.algorithm].
     *
     * @param alias The alias of the key to sign with.
     * @param dataToSign the data to sign.
     * @param unlockReason the reason for unlocking.
     * @return the signature.
     * @throws IllegalArgumentException if there is no key with the given alias
     * or the key wasn't created with a signing algorithm.
     * @throws IllegalArgumentException if the signature algorithm isn’t compatible with the key.
     * @throws KeyLockedException if the key needs unlocking.
     * @throws KeyInvalidatedException if the key is no longer usable.
     */
    @Throws(
        IllegalArgumentException::class,
        KeyLockedException::class,
        KeyInvalidatedException::class,
        CancellationException::class
    )
    suspend fun sign(
        alias: String,
        dataToSign: ByteArray,
        unlockReason: Reason = Reason.Unspecified
    ): Signature

    /**
     * Performs Key Agreement.
     *
     * Key agreement is only supported for EC keys.
     *
     * If the key needs unlocking before use (for example user authentication
     * in any shape or form) and `keyUnlockData` isn't set or doesn't contain
     * what's needed, [KeyLockedException] is thrown.
     *
     * @param alias the alias of the EC key to use.
     * @param otherKey The public EC key from the other party
     * @param unlockReason the reason for unlocking.
     * @return The shared secret.
     * @throws IllegalArgumentException if the key is not an EC key or the other key isn't the same curve.
     * @throws IllegalArgumentException if there is no key with the given alias
     * or the key wasn't created with a key agreement algorithm.
     * @throws KeyLockedException if the key needs unlocking.
     * @throws KeyInvalidatedException if the key is no longer usable.
     */
    @Throws(
        IllegalArgumentException::class,
        KeyLockedException::class,
        KeyInvalidatedException::class,
        CancellationException::class
    )
    suspend fun keyAgreement(
        alias: String,
        otherKey: EcPublicKey,
        unlockReason: Reason = Reason.Unspecified
    ): SecureByteString

    /**
     * Performs Key Decapsulation.
     *
     * Key decapsulation is only supported for KEM keys (such as ML-KEM).
     *
     * If the key needs unlocking before use (for example user authentication
     * in any shape or form) and `keyUnlockData` isn't set or doesn't contain
     * what's needed, [KeyLockedException] is thrown.
     *
     * @param alias the alias of the KEM key to use.
     * @param ciphertext The encapsulated ciphertext from the sender.
     * @param unlockReason the reason for unlocking.
     * @return The decapsulated shared secret as a [SecureByteString].
     * @throws IllegalArgumentException if the key is not a KEM key.
     * @throws IllegalArgumentException if there is no key with the given alias
     * or the key wasn't created with a KEM algorithm.
     * @throws KeyLockedException if the key needs unlocking.
     * @throws KeyInvalidatedException if the key is no longer usable.
     * @throws UnsupportedOperationException if this Secure Area does not support key decapsulation.
     */
    @Throws(
        IllegalArgumentException::class,
        KeyLockedException::class,
        KeyInvalidatedException::class,
        UnsupportedOperationException::class,
        CancellationException::class
    )
    suspend fun kemDecapsulate(
        alias: String,
        ciphertext: ByteArray,
        unlockReason: Reason = Reason.Unspecified
    ): SecureByteString {
        throw UnsupportedOperationException("Key decapsulation is not supported by this SecureArea")
    }

    /**
     * Gets information about a key.
     *
     * This works even on keys that are invalidated.
     *
     * @param alias the alias of the key to use.
     * @return a [KeyInfo] object.
     * @throws IllegalArgumentException if there is no key with the given alias.
     */
    @Throws(IllegalArgumentException::class, CancellationException::class)
    suspend fun getKeyInfo(alias: String): KeyInfo

    /**
     * Checks whether the key has been invalidated.
     *
     * @param alias the alias of the key to check for.
     * @return `true` if the key has been invalidated, `false` otherwise.
     * @throws IllegalArgumentException if there is no key with the given alias.
     */
    @Throws(IllegalArgumentException::class, CancellationException::class)
    suspend fun getKeyInvalidated(alias: String): Boolean

    /**
     * Unlocks a key requiring user authentication.
     *
     * Returns an empty list if user authentication is not required to unlock the key.
     * Otherwise, it performs user authentication and returns a list of [KeyUnlockData] objects
     * which can be stored in a [KeyUnlockDataProvider] and returned as appropriate.
     *
     * @param alias the alias of the key to unlock.
     * @param unlockReason the reason for unlocking.
     * @return a list of [KeyUnlockData] objects if user authentication was required and performed, or empty list if not required.
     * @throws IllegalArgumentException if there is no key with the given alias.
     * @throws KeyLockedException if user authentication was canceled or failed.
     */
    @Throws(
        IllegalArgumentException::class,
        KeyLockedException::class,
        CancellationException::class
    )
    suspend fun unlockKey(
        alias: String,
        unlockReason: Reason = Reason.Unspecified
    ): List<KeyUnlockData>
}